/**
 * Curl 转换器主应用入口
 */

import { UIController } from './ui/components.js';

// 全局错误处理
window.addEventListener('error', (event) => {
  console.error('应用错误:', event.error);
  // 可以在这里添加错误报告逻辑
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('未处理的 Promise 拒绝:', event.reason);
  // 可以在这里添加错误报告逻辑
});

/**
 * 应用类
 */
class CurlConverterApp {
  constructor() {
    this.uiController = null;
    this.initialized = false;
  }

  /**
   * 初始化应用
   * @returns {Promise<void>}
   */
  async init() {
    try {
      console.log('🚀 正在启动 Curl 转换器...');

      // 显示加载状态
      this.showLoadingState();

      // 创建 UI 控制器
      this.uiController = new UIController();

      // 初始化 UI
      await this.uiController.init();

      // 隐藏加载状态
      this.hideLoadingState();

      this.initialized = true;
      console.log('✅ Curl 转换器启动完成');

      // 添加一些有用的提示
      this.showStartupTips();

    } catch (error) {
      console.error('❌ 应用启动失败:', error);
      this.showErrorState(error);
    }
  }

  /**
   * 显示加载状态
   */
  showLoadingState() {
    const loadingElement = document.querySelector('.loading-screen');
    if (loadingElement) {
      loadingElement.style.display = 'flex';
    }
  }

  /**
   * 隐藏加载状态
   */
  hideLoadingState() {
    const loadingElement = document.querySelector('.loading-screen');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
  }

  /**
   * 显示错误状态
   * @param {Error} error - 错误对象
   */
  showErrorState(error) {
    this.hideLoadingState();

    const appElement = document.getElementById('app');
    if (appElement) {
      appElement.innerHTML = `
        <div class="error-state">
          <div class="error-content">
            <h2>❌ 启动失败</h2>
            <p>抱歉，应用启动时遇到了问题。</p>
            <details>
              <summary>错误详情</summary>
              <pre>${error.message}</pre>
            </details>
            <button onclick="window.location.reload()" class="btn btn-primary">
              🔄 刷新页面
            </button>
          </div>
        </div>
      `;
    }
  }

  /**
   * 显示启动提示
   */
  showStartupTips() {
    // 可以在这里添加一些用户友好的提示
    setTimeout(() => {
      console.log(`
📚 使用提示:
• 在左侧输入框中输入 curl 命令
• 选择目标编程语言
• 代码会自动生成在右侧
• 使用 Ctrl+Enter 快速转换
• 点击示例按钮查看常用命令

🎯 支持的功能:
• HTTP 方法 (GET, POST, PUT, DELETE, etc.)
• 请求头和认证
• 请求体数据
• Cookie 和会话
• 文件上传和代理
      `);
    }, 1000);
  }

  /**
   * 获取应用状态
   * @returns {Object} 应用状态
   */
  getState() {
    return {
      initialized: this.initialized,
      uiController: this.uiController ? this.uiController.getState() : null,
      version: '1.0.0',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * 销毁应用
   */
  destroy() {
    if (this.uiController) {
      this.uiController.destroy();
      this.uiController = null;
    }
    this.initialized = false;
  }
}

// 创建全局应用实例
let app = null;

/**
 * 启动应用
 */
function startApp() {
  if (app) {
    console.warn('应用已经启动');
    return;
  }

  app = new CurlConverterApp();
  app.init();
}

/**
 * 获取应用实例
 * @returns {CurlConverterApp|null} 应用实例
 */
function getApp() {
  return app;
}

/**
 * 重启应用
 */
function restartApp() {
  if (app) {
    app.destroy();
  }
  startApp();
}

// 页面加载完成后启动应用
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startApp);
} else {
  // 页面已经加载完成
  startApp();
}

// 导出用于调试
window.CurlConverterApp = CurlConverterApp;
window.getApp = getApp;
window.restartApp = restartApp;

// 服务工作线程注册（用于 PWA 支持）
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // 可以在这里注册 service worker 用于离线支持
    // navigator.serviceWorker.register('/sw.js');
  });
}

// 性能监控
if ('performance' in window && 'PerformanceObserver' in window) {
  try {
    // 监控长任务
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 50) { // 长任务阈值 50ms
          console.warn('检测到长任务:', entry);
        }
      }
    });
    observer.observe({ entryTypes: ['longtask'] });
  } catch (e) {
    // 浏览器不支持长任务监控
  }
}